import { Observable, Subject, fromEventPattern, merge } from 'rxjs';
import { map, takeUntil, shareReplay, catchError } from 'rxjs/operators';

import { Inject, Injectable } from '@angular/core';

import {
  IMarker as _IMarker,
  IMarkerEventUnion,
  IMarkerClickEvent,
  IMarkerDoubleClickEvent,
  IMarkerRightClickEvent,
  IMarkerMouseOverEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerDragStartEvent,
  IMarkerDragMoveEvent,
  IMarkerDragEndEvent,
  IMarkerPositionChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
  IMarkerSelectionChangeEvent,
  IMarkerIconChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerAddEvent,
  IMarkerRemoveEvent,
  IMarkerDestroyEvent,
  MarkerEventType,
} from '@lib/geo/map';

import { EsriMap } from '../../../map';
import { EsriModules, ESRI_MODULES_TOKEN } from '../../../tokens';
import { EventConfigMap, IEsriMarker } from '../interfaces';

type IMarker = _IMarker<EsriMap>

@Injectable()
export class EsriMarkerEventService {
  private readonly _destroy$ = new Subject<void>();
  private readonly _esriMarkers = new Map<string, IEsriMarker>();

  private readonly _programmaticEvents$ = new Subject<IMarkerEventUnion<EsriMap>>();

  private readonly _eventConfigs: EventConfigMap<EsriMap> = {
    click: {
      esriEventName: 'click',
      createEventData: (marker) => ({
        event: new MouseEvent('click'),
        coordinates: { lat: marker.position.lat, lng: marker.position.lon },
        button: 'left' as const,
      }),
    },
    doubleclick: {
      esriEventName: 'double-click',
      createEventData: (marker) => ({
        event: new MouseEvent('dblclick'),
        coordinates: { lat: marker.position.lat, lng: marker.position.lon },
        button: 'left' as const,
      }),
    },
    rightclick: {
      esriEventName: 'right-click',
      createEventData: (marker) => ({
        event: new MouseEvent('contextmenu'),
        coordinates: { lat: marker.position.lat, lng: marker.position.lon },
        button: 'right' as const,
      }),
    },
    mouseover: {
      esriEventName: 'mouse-over',
      createEventData: (marker) => ({
        event: new MouseEvent('mouseover'),
        coordinates: { lat: marker.position.lat, lng: marker.position.lon },
      }),
    },
    mouseout: {
      esriEventName: 'mouse-out',
      createEventData: (marker) => ({
        event: new MouseEvent('mouseout'),
        coordinates: { lat: marker.position.lat, lng: marker.position.lon },
      }),
    },
    mouseenter: {
      esriEventName: 'mouse-enter',
      createEventData: (marker) => ({
        event: new MouseEvent('mouseenter'),
        coordinates: { lat: marker.position.lat, lng: marker.position.lon },
      }),
    },
    mouseleave: {
      esriEventName: 'mouse-leave',
      createEventData: (marker) => ({
        event: new MouseEvent('mouseleave'),
        coordinates: { lat: marker.position.lat, lng: marker.position.lon },
      }),
    },
    dragstart: {
      esriEventName: 'drag-start',
      createEventData: (marker) => ({
        event: new MouseEvent('dragstart'),
        position: marker.position,
        delta: { lat: 0, lng: 0 },
      }),
    },
    drag: {
      esriEventName: 'drag',
      createEventData: (marker) => ({
        event: new MouseEvent('drag'),
        position: marker.position,
        delta: { lat: 0, lng: 0 },
      }),
    },
    dragend: {
      esriEventName: 'drag-end',
      createEventData: (marker) => ({
        event: new MouseEvent('dragend'),
        position: marker.position,
        delta: { lat: 0, lng: 0 },
      }),
    },
    positionchange: {
      esriEventName: 'position-change',
      createEventData: (marker) => ({
        previous: { position: marker.position },
        current: { position: marker.position },
      }),
    },
    titlechange: {
      esriEventName: 'title-change',
      createEventData: (marker) => ({
        previous: { title: marker.title },
        current: { title: marker.title },
      }),
    },
    visibilitychange: {
      esriEventName: 'visibility-change',
      createEventData: (marker) => ({
        previous: { visible: marker.visible },
        current: { visible: marker.visible },
      }),
    },
    selectionchange: {
      esriEventName: 'selection-change',
      createEventData: (marker) => ({
        previous: { selected: marker.selected },
        current: { selected: marker.selected },
      }),
    },
    iconchange: {
      esriEventName: 'icon-change',
      createEventData: (marker) => ({
        previous: { icon: marker.icon },
        current: { icon: marker.icon },
      }),
    },
    stylechange: {
      esriEventName: 'style-change',
      createEventData: (marker) => ({
        previous: { styles: marker.styles },
        current: { styles: marker.styles },
      }),
    },
    add: {
      esriEventName: 'add',
      createEventData: (marker) => ({
        reason: 'programmatic',
        markerData: marker,
      }),
    },
    remove: {
      esriEventName: 'remove',
      createEventData: (marker) => ({
        reason: 'programmatic',
        markerData: marker,
      }),
    },
    destroy: {
      esriEventName: 'destroy',
      createEventData: (marker) => ({
        reason: 'programmatic',
        markerData: marker,
      }),
    },
  };

  constructor(@Inject(ESRI_MODULES_TOKEN) private readonly esri: EsriModules) {}

  public setMarkersCollection(markers: Map<string, IEsriMarker>): void {
    this._esriMarkers.clear();
    markers.forEach((marker, id) => {
      this._esriMarkers.set(id, marker);
    });
  }

  public onMarkerClick(marker: IMarker): Observable<IMarkerClickEvent<EsriMap, IMarker>> {
    return this._createEventObservable('click', marker);
  }

  public onMarkerDoubleClick(marker: IMarker): Observable<IMarkerDoubleClickEvent<EsriMap, IMarker>> {
    return this._createEventObservable('doubleclick', marker);
  }

  public onMarkerRightClick(marker: IMarker): Observable<IMarkerRightClickEvent<EsriMap, IMarker>> {
    return this._createEventObservable('rightclick', marker);
  }

  public onMarkerMouseOver(marker: IMarker): Observable<IMarkerMouseOverEvent<EsriMap, IMarker>> {
    return this._createEventObservable('mouseover', marker);
  }

  public onMarkerMouseOut(marker: IMarker): Observable<IMarkerMouseOutEvent<EsriMap, IMarker>> {
    return this._createEventObservable('mouseout', marker);
  }

  public onMarkerMouseEnter(marker: IMarker): Observable<IMarkerMouseEnterEvent<EsriMap, IMarker>> {
    return this._createEventObservable('mouseenter', marker);
  }

  public onMarkerMouseLeave(marker: IMarker): Observable<IMarkerMouseLeaveEvent<EsriMap, IMarker>> {
    return this._createEventObservable('mouseleave', marker);
  }

  public onMarkerDragStart(marker: IMarker): Observable<IMarkerDragStartEvent<EsriMap, IMarker>> {
    return this._createEventObservable('dragstart', marker);
  }

  public onMarkerDrag(marker: IMarker): Observable<IMarkerDragMoveEvent<EsriMap, IMarker>> {
    return this._createEventObservable('drag', marker);
  }

  public onMarkerDragEnd(marker: IMarker): Observable<IMarkerDragEndEvent<EsriMap, IMarker>> {
    return this._createEventObservable('dragend', marker);
  }

  public onMarkerPositionChange(marker: IMarker): Observable<IMarkerPositionChangeEvent<EsriMap, IMarker>> {
    return this._createEventObservable('positionchange', marker);
  }

  public onMarkerTitleChange(marker: IMarker): Observable<IMarkerTitleChangeEvent<EsriMap, IMarker>> {
    return this._createEventObservable('titlechange', marker);
  }

  public onMarkerVisibilityChange(
    marker: IMarker,
  ): Observable<IMarkerVisibilityChangeEvent<EsriMap, IMarker>> {
    return this._createEventObservable('visibilitychange', marker);
  }

  public onMarkerSelectionChange(
    marker: IMarker,
  ): Observable<IMarkerSelectionChangeEvent<EsriMap, IMarker>> {
    return this._createEventObservable('selectionchange', marker);
  }

  public onMarkerIconChange(marker: IMarker): Observable<IMarkerIconChangeEvent<EsriMap, IMarker>> {
    return this._createEventObservable('iconchange', marker);
  }

  public onMarkerStyleChange(marker: IMarker): Observable<IMarkerStyleChangeEvent<EsriMap, IMarker>> {
    return this._createEventObservable('stylechange', marker);
  }

  public onMarkerAdd(marker: IMarker): Observable<IMarkerAddEvent<EsriMap, IMarker>> {
    return this._createEventObservable('add', marker);
  }

  public onMarkerRemove(marker: IMarker): Observable<IMarkerRemoveEvent<EsriMap, IMarker>> {
    return this._createEventObservable('remove', marker);
  }

  public onMarkerDestroy(marker: IMarker): Observable<IMarkerDestroyEvent<EsriMap, IMarker>> {
    return this._createEventObservable('destroy', marker);
  }

  public onMarkerEvents(marker: IMarker): Observable<IMarkerEventUnion<EsriMap>> {
    const eventObservables = Object.keys(this._eventConfigs).map((eventType) =>
      this._createEventObservable(eventType as MarkerEventType, marker),
    );

    return merge(...eventObservables).pipe(
      catchError((error) => {
        console.error('Error in marker event stream:', error);
        return new Observable<IMarkerEventUnion<EsriMap>>();
      }),
    ) as Observable<IMarkerEventUnion<EsriMap>>;
  }

  public fireMarkerEvent(eventType: string, marker: IMarker, eventData?: unknown): void {
    const event: IMarkerEventUnion<EsriMap> = {
      type: eventType as MarkerEventType,
      marker,
      timestamp: Date.now(),
      data: eventData || {},
    } as IMarkerEventUnion<EsriMap>;

    this._programmaticEvents$.next(event);
  }

  public onProgrammaticEvents(): Observable<IMarkerEventUnion<EsriMap>> {
    return this._programmaticEvents$.asObservable().pipe(takeUntil(this._destroy$));
  }

  public checkMarkerStateChanges(marker: IMarker): void {
    const esriMarker = this._esriMarkers.get(marker.id);

    if (!esriMarker) {
      return;
    }
  }

  public destroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._programmaticEvents$.complete();
    this._esriMarkers.clear();
  }

  private _createEventObservable<T extends IMarkerEventUnion<EsriMap>>(
    eventType: MarkerEventType,
    marker: IMarker,
  ): Observable<T> {
    const esriMarker = this._esriMarkers.get(marker.id);
    if (!esriMarker) {
      return new Observable<T>((subscriber) => {
        subscriber.complete();
      });
    }

    const config = this._eventConfigs[eventType];
    if (!config) {
      return new Observable<T>((subscriber) => {
        subscriber.complete();
      });
    }

    return fromEventPattern<T>(
      (handler) => {
        const graphic = esriMarker.getMarker();
        if (graphic && 'on' in graphic) {
          (graphic as unknown as { on: (event: string, handler: unknown) => void }).on(
            config.esriEventName,
            handler,
          );
        }
      },
      (handler) => {
        const graphic = esriMarker.getMarker();
        if (graphic && 'off' in graphic) {
          (graphic as unknown as { off: (event: string, handler: unknown) => void }).off(
            config.esriEventName,
            handler,
          );
        }
      },
    ).pipe(
      map(() => this._createEvent<T>(eventType, marker)),
      takeUntil(this._destroy$),
      shareReplay(1),
      catchError((error) => {
        console.error(`Error in ${eventType} event for marker ${marker.id}:`, error);
        throw error;
      }),
    );
  }

  private _createEvent<T extends IMarkerEventUnion<EsriMap>>(
    eventType: MarkerEventType,
    marker: IMarker,
  ): T {
    const config = this._eventConfigs[eventType];
    return {
      type: eventType,
      marker,
      timestamp: Date.now(),
      data: config.createEventData(marker),
    } as unknown as T;
  }
}
